import { Alert, Field, Input, Button, Section } from "@/components/ui";
import type { CouponPreview } from "@/lib/domains";
import { formatMoney } from "@/lib/format";
import { ApiError, errorMessage } from "@/lib/http";

/** The refusals checkout gives for a promotion code (each is about the code, not about paying). */
const COUPON_REASONS = new Set([
  "COUPON_INVALID",
  "COUPON_EXPIRED",
  "COUPON_INELIGIBLE",
  "COUPON_MINIMUM_NOT_MET",
  "COUPON_EXHAUSTED",
  "COUPON_ALREADY_REDEEMED",
]);

export const isCouponReason = (reason: string | null | undefined): boolean =>
  reason != null && COUPON_REASONS.has(reason);

/** What to say under the field: the server's message for the `coupon_code` field, else the error's own. */
export function couponFieldError(err: unknown): string {
  return (err instanceof ApiError ? err.fieldError("coupon_code") : undefined) ?? errorMessage(err);
}

export interface PromotionCodeProps {
  code: string;
  onCode: (code: string) => void;
  onApply: () => void;
  /** The server's decision for the code in the field, once it has been checked. */
  applied: CouponPreview | null;
  /** Why the code was refused (shown under the field), or null. */
  error: string | null;
  /** A check is under way. */
  pending: boolean;
  /** Something else (such as the payment) is under way. */
  disabled?: boolean;
}

/** The checkout's promotion code: the field, its reason when refused, and what the code takes off. */
export function PromotionCode({
  code,
  onCode,
  onApply,
  applied,
  error,
  pending,
  disabled,
}: PromotionCodeProps) {
  const apply = () => {
    if (code.trim().length >= 3 && !pending && !disabled) onApply();
  };
  return (
    <Section title="Promotion code" subtitle="Optional — codes are checked again when you pay.">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <Field
          label="Code"
          hint="Enter the code exactly as supplied by the seller."
          error={error ?? undefined}
        >
          {(field) => (
            <Input
              size="lg"
              {...field}
              value={code}
              maxLength={32}
              autoCapitalize="characters"
              autoComplete="off"
              placeholder="e.g. WELCOME25"
              invalid={error !== null}
              onChange={(e) => onCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault(); // the checkout is a form: Enter here applies the code, it doesn't pay
                apply();
              }}
            />
          )}
        </Field>
        <Button
          size="lg"
          variant="secondary"
          loading={pending}
          disabledReason={
            disabled
              ? "Wait for your cart to finish updating"
              : code.trim().length < 3
                ? "Enter a promotion code of at least 3 characters"
                : undefined
          }
          onClick={apply}
        >
          Apply code
        </Button>
      </div>
      {error !== null && (
        <Alert tone="danger">That promotion code couldn't be applied. Your order total hasn't changed.</Alert>
      )}
      {applied && (
        <p className="text-sm text-[#007955]" role="status">
          {applied.code} applied — {formatMoney(applied.discount_cents, applied.currency)} off.
        </p>
      )}
    </Section>
  );
}
